// src/pages/Dashboard.tsx
import { DownloadIcon, FileTextIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useExportDocument } from "../hooks/useExportDocument";
import { useIES } from "../hooks/useIES";
import { RootState } from "../redux/store";
import { getBlobV2 } from "../utils/axios-config";
import { Manuals } from "../utils/constants";

const Dashboard: React.FC = () => {
  const userData = useSelector((state: RootState) => state.login.userData);
  const { downloadOnly } = useExportDocument();
  const { IES } = useIES();
  const [isDownloading, setIsDownloading] = useState(false);

  const getManualType = () => {
    const rawRole = userData?.Role;
    const role = Array.isArray(rawRole) ? rawRole[0] ?? "" : rawRole ?? "";
    const normalized = String(role).toLowerCase();

    if (normalized === "administrador" || normalized === "admin") {
      return "manual-administrador";
    }
    if (normalized === "docente") {
      return "manual-docente";
    }
    if (normalized === "estudante") {
      return "manual-usuario";
    }
    return undefined;
  };

  const manualType = getManualType();

  const handleDownload = async () => {
    if (!manualType) return;
    setIsDownloading(true);
    try {
      const blob = await getBlobV2(`${Manuals}/${manualType}`);
      const fileName = `${manualType}.pdf`;
      await downloadOnly(blob, fileName);
      toast.success("Manual descarregado com sucesso.");
    } catch (error: any) {
      console.error("Erro ao descarregar o manual:", error);
      toast.error("Erro ao descarregar o manual. Por favor, tente novamente.");
    } finally {
      setIsDownloading(false);
    }
  };

  const firstName = userData?.Fullname?.split(" ")?.[0] ?? "";
  const institution = IES.NAME !== "N/A" ? IES.NAME : "e-Sura";

  return (
    <Flex direction="column" gap="4">
      <Card size="3" variant="surface">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="5" weight="bold">
            Bem-vindo{firstName ? `, ${firstName}` : ""}
          </Heading>
          <Text size="2" color="gray">
            {institution} — use o menu lateral para aceder às funcionalidades disponíveis
            para o seu perfil.
          </Text>
        </Flex>
      </Card>

      {/* O manual só existe para alguns perfis; sem ele, o painel continua útil. */}
      {manualType && (
        <Card size="3" variant="surface">
          <Flex
            direction={{ initial: "column", sm: "row" }}
            align={{ initial: "start", sm: "center" }}
            justify="between"
            gap="4"
          >
            <Flex gap="3" align="start">
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                  borderRadius: "var(--radius-3)",
                  backgroundColor: "var(--accent-3)",
                  color: "var(--accent-11)",
                }}
              >
                <FileTextIcon width="20" height="20" />
              </Box>

              <Flex direction="column" gap="1">
                <Flex align="center" gap="2">
                  <Heading as="h3" size="3" weight="bold">
                    Manual do utilizador
                  </Heading>
                  <Badge color="green" variant="soft">
                    Novo
                  </Badge>
                </Flex>
                <Text size="2" color="gray">
                  Guia completo com instruções detalhadas sobre como utilizar a
                  plataforma e tirar o máximo proveito das funcionalidades.
                </Text>
              </Flex>
            </Flex>

            <Button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              style={{ flexShrink: 0 }}
            >
              <DownloadIcon /> {isDownloading ? "A descarregar…" : "Descarregar"}
            </Button>
          </Flex>
        </Card>
      )}
    </Flex>
  );
};

export default Dashboard;
